import { SettingsOutlined } from "@mui/icons-material";
import { Typography, Chip } from "@mui/material";
import TextInput from "../../Component/UI/TextInput/TextInput";
import lodash from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import MUIButton from "../../Component/UI/MUIButton/MUIButton";
import MUISelect from "../../Component/UI/MUISelect/MUISelect";
import MUIMultiSelect from "../../Component/UI/MUIMultiSelect/MUIMultiSelect";
import MUIMultiItemBox from "../../Component/UI/MUIMultiItemBox/MUIMultiItemBox";
import MUIIconButton from "../../Component/UI/MUIIconButton/MUIIconButton";
import Modal from "../../Component/Modal/Modal";
import Warning from "../../Component/Warning/Warning";
import { DefaultSet, OVERVIEW_LLM_LABELS } from "../../utils/constant";
import { validateBrandInputValue } from "../../utils/helpers";
import { AuthContext } from "../../context/auth-context";
import { updatePersona, createPersona, fetchPersonas } from "../../redux/actions/personasActions";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import CAHistory from "./CAHistory";
import CASettingsModal from "./Settings/Setting";
import useCAHistory from "./useCAHistory";
import useCompetitorSuggestions from "./useCompetitorSuggestions";
import { useNavigate } from "react-router-dom";
import RadioGroup from "../../Component/UI/RadioGroup/RadioGroup";
import { DiscoveryContext as BrandAuditDiscoveryContext } from "../BrandAudit/discovery-context";
import { CategoryAnalysisContext } from "./category-analysis-context";
import { ALL_METRIC_VALUES } from "./metricOptions";
import classes from "../BrandAudit/Form.module.css";
import { resolveDefaultKeywordOptions } from "../../utils/keywordOptions";
import { resolveDefaultPersonaOptions, resolveMaxPersonas } from "../../utils/personaOptions";
import { hasBatchCapableLlm } from "../../utils/batchCapableLlms";
import { isFlagOn, isAssessmentUiOn } from "../../utils/navFlags";

const CA_MODE_OPTIONS = [
  { value: "baseline", name: "Test Analysis" },
  { value: "full", name: "Full Analysis" },
];

const BASELINE_LLM_FALLBACK_ORDER = ["gpt-6-luna", "gpt-4.1-nano"];

function getBaselineLLMOptions(llmOptions) {
  const options = llmOptions || [];
  for (const value of BASELINE_LLM_FALLBACK_ORDER) {
    const match = options.find((opt) => opt.value === value);
    if (match) return [match];
  }
  return [];
}

const MAX_COMPETITORS_OPTIONS = 10;

export default function CAForm({
  onSubmit,
  onReset = () => { },
  onStartNew = () => { },
  warningText,
  setWarningText = () => { },
  cancelTransaction,
  setCancelTransaction = () => { },
  stopPolling = () => { },
  startPolling = () => { },
  setLaunchType = () => { },
  launchType = null,
  setIsAuditRunning = () => { },
  setActiveTab = () => { },
  loadCategoryAnalysis,
  isAuditRunning = false,
  processRetry = false,
  caMode = "baseline",
  onCAModeChange = () => { },
  isBaselineCategoryAnalysisEnabled = false,
  onBaselineCAHtmlReportChange = () => { },
}) {
  const dispatch = useDispatch();
  const caCtx = useContext(CategoryAnalysisContext);
  const brandAuditCtx = useContext(BrandAuditDiscoveryContext);
  const navigate = useNavigate();
  const { user, selectedCustomerId, customerUiInfo } = useContext(AuthContext);

  // Category Analysis is the "Category" option of Brand Audit; Brand/Product switch back to that page.
  const AUDIT_TYPE_OPTIONS = [
    { value: "brand", label: ENTITY_LABELS.brand },
    ...(ENTITY_LABELS.hasProductEntity ? [{ value: "product", label: ENTITY_LABELS.product }] : []),
    { value: "category", label: "Category" },
  ];
  const handleAuditTypeChange = (value) => {
    if (value === "category") return;
    caCtx.resetState();
    caCtx.resetFormState(true);
    brandAuditCtx.resetState();
    brandAuditCtx.updateType(value);
    brandAuditCtx.resetFormState(true);
    navigate("/luminaire/brand-audit");
  };
  const userMetricSetting = useSelector((store) => store.userSet);
  const customerPersonasData = useSelector((store) => store.personas?.data?.data);
  const { data: rawLlmOptions, loading: llmOptionsLoading } = useSelector((store) => store.llmOptions);
  const fullLlmOptions = rawLlmOptions?.filter((opt) => opt.value !== "gpt-6-luna") ?? rawLlmOptions;
  const baselineLlmOptions = getBaselineLLMOptions(rawLlmOptions);
  const llmOptions = caMode === "baseline" ? baselineLlmOptions : fullLlmOptions;
  const getDefaultLLM = (mode = caMode) =>
    mode === "baseline" ? (baselineLlmOptions[0]?.value || BASELINE_LLM_FALLBACK_ORDER[0]) : "gpt-5.6";
  const defaultUseMetricSettingID = userMetricSetting.brandAuditMetricSettingId;
  const testLLMModel = process.env.REACT_APP_LOWCOST_MODEL;
  const isNewUser = !caCtx?.state?.transactionId;
  const isFinalLaunchEnabled = user?.is_categoryanalysis_final_launch_enable !== false;
  const isMultiCategoryEnabled = user?.is_multi_category_enabled || false;
  const MAX_CATEGORIES = 3;
  const isTestAnalysisRunning = caCtx?.loading &&
    Array.isArray(caCtx.llm) &&
    caCtx.llm.length === 1 &&
    caCtx.llm[0] === testLLMModel;
  const shouldLockToTestLLM = (isNewUser && !isFinalLaunchEnabled) || isNewUser || isTestAnalysisRunning;
  const displayLlmOptions = shouldLockToTestLLM
    ? rawLlmOptions?.map(opt => opt.value === testLLMModel ? opt : {
        ...opt,
        is_visible: false,
        lockReason: isFinalLaunchEnabled ? "Only available for the Final Analysis" : undefined,
      })
    : llmOptions;

  // Mode-based limits
  const DEFAULT_PRESELECT = 3;
  const MAX_LLMS = caMode === "baseline" ? 3 : 6;

  // Form state
  const [llm, setLLM] = useState([getDefaultLLM()]);
  const [isBatchMode, setIsBatchMode] = useState(false);
  useEffect(() => {
    if (!hasBatchCapableLlm(llm)) setIsBatchMode(false);
  }, [llm]);
  const [primaryLLM, setPrimaryLLM] = useState(getDefaultLLM());
  const [category, setCategory] = useState([]); // [{title: "Cat Name"}] objects
  const [market, setMarket] = useState(["General Market"]);
  const [persona, setPersona] = useState([{ title: "General Persona", description: "General Persona" }]);
  const maxPersonas = resolveMaxPersonas(user?.email);
  const [metricSettingId, setMetricSettingId] = useState(defaultUseMetricSettingID);
  const [rbiType, setRbiType] = useState("propensityToRecommend");
  const [keywords, setKeywords] = useState(() => resolveDefaultKeywordOptions(customerUiInfo));
  const [rankCompetitiveSet, setRankCompetitiveSet] = useState([]);
  const [rankMethodology, setRankMethodology] = useState("by_category");
  const [selectedMetrics, setSelectedMetrics] = useState(ALL_METRIC_VALUES);
  const isVisibilityOnlyRun = selectedMetrics.length === 1 && selectedMetrics[0] === "rank";
  const isUncappedCompetitorAccount = user?.email === "lifescience@yopmail.com";
  const isSpecialAccount = user?.email === "demo@yopmail.com";
  const MAX_COMPETITORS = isSpecialAccount
    ? 15
    : isUncappedCompetitorAccount || isVisibilityOnlyRun
      ? 10
      : caMode === "baseline"
        ? 3
        : 5;
  const [personaSettings, setPersonaSettings] = useState({
    applyPersonaToAllCategory: false,
    applyPersonaToAllMarkets: false,
  });
  const [personaMap, setPersonaMap] = useState(() =>
    Object.fromEntries(resolveDefaultPersonaOptions(customerUiInfo).map((name) => [name, name]))
  );
  const [personaEditModalOpen, setPersonaEditModalOpen] = useState(false);
  const [editingPersonaTitle, setEditingPersonaTitle] = useState(null);
  const [editingCustomerPersonaId, setEditingCustomerPersonaId] = useState(null);

  // Competitor state
  const [competitorsData, setCompetitorsData] = useState({});
  const [selectedBrandsProducts, setSelectedBrandProducts] = useState({});
  const [customCompetitors, setCustomCompetitors] = useState({});
  const [customCompetitorValues, setCustomCompetitorValues] = useState({});
  const [updatedKeyPlayers, setUpdatedKeyPlayers] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  const [competitorWarning, setCompetitorWarning] = useState("");

  // UI state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [categoryInputText, setCategoryInputText] = useState("");
  const categoryInputDebounceRef = useRef(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [launchMessage, setLaunchMessage] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [categoryWiseMetric, setCategoryWiseMetric] = useState({});
  const [categoryMetricChange, setCategoryWiseMetricChange] = useState([]);

  const abortControllerRef = useRef(null);
  const competitorValidationAbortRef = useRef(null);

  const { keyPlayers, setKeyPlayers, loading: keyPlayersLoading, getCompetitorsForCategory } =
    useCompetitorSuggestions();
  const { deleteHistory, getHistory } = useCAHistory();
  const { page } = caCtx;

  const isFinalTransactionCompleted =
    caCtx?.state?.finalTransactionStatus === "COMPLETED" ||
    caCtx?.state?.finalTransactionStatus === "RETRY";
  const shouldResetOnDelete =
    caCtx?.state?.transactionId === caCtx?.state?.finalTransactionId ||
    (caCtx?.state?.transactionStatus !== "RUNNING" && caCtx?.state?.launchRecord?.length === 1);
  const isTransactionPending = caCtx?.state?.transactionStatus === "PENDING";
  const auditTransactionId = caCtx.state.id;

  const _caTemplateBaseOptions = userMetricSetting?.data?.map((t) => ({ name: t.name, value: t.id, isDefault: t.isDefault })) ?? [];
  const _caTemplateIsDisabled = caCtx?.loading || isFinalTransactionCompleted || isTransactionPending;
  const _caTemplateMetricObj = caCtx.state?.metricSettingObject ?? {};
  const _caTemplateKnownValues = new Set(_caTemplateBaseOptions.map((o) => String(o.value)));
  const _caTemplateExtraOptions = _caTemplateIsDisabled
    ? Object.values(_caTemplateMetricObj)
      .filter((entry) => entry?.id != null && !_caTemplateKnownValues.has(String(entry.id)))
      .map((entry) => ({ name: entry.name, value: entry.id === "default" ? "default" : Number(entry.id), isDefault: entry.isDefault ?? false }))
    : [];
  const caTemplateOptions = [..._caTemplateBaseOptions, ..._caTemplateExtraOptions];

  const personaOptions = Object.keys(personaMap).map((title) => ({
    name: title === "General Persona" ? `General ${ENTITY_LABELS.persona}` : title,
    value: title,
  }));
  const selectedPersonaTitles = persona
    .map((p) => (typeof p === "string" ? p : p?.title))
    .filter(Boolean);

  // Convert category objects to plain strings for the API; fall back to typed-but-uncommitted input
  const categoryStrings = category.length > 0
    ? category.map((c) => (typeof c === "string" ? c : c?.category || c?.title || c?.market || "")).filter(Boolean)
    : (categoryInputText.trim() ? [categoryInputText.trim()] : []);
  // Falls back to typed-but-not-committed input so buttons/validation work without requiring Enter
  const effectiveCategoryStrings = categoryStrings.length > 0 ? categoryStrings : (categoryInputText.trim() ? [categoryInputText.trim()] : []);

  // Load personas for the current customer
  useEffect(() => {
    if (caCtx?.state?.transactionId) return;
    setMetricSettingId(defaultUseMetricSettingID);
  }, [defaultUseMetricSettingID]);

  useEffect(() => {
    if (!customerPersonasData || customerPersonasData.length === 0) return;
    const converted = customerPersonasData.reduce((acc, item) => {
      const name = item?.persona?.name;
      const description = item?.persona?.description;
      if (name) acc[name] = description || name;
      return acc;
    }, {});
    setPersonaMap({
      ...Object.fromEntries(resolveDefaultPersonaOptions(customerUiInfo).map((name) => [name, name])),
      ...converted,
    });
  }, [customerPersonasData, customerUiInfo]);

  const userRefetchCatsRef = useRef(new Set());
  const preRefetchSnapshotRef = useRef({});

  // Sync keyPlayers into competitor data
  useEffect(() => {
    const updatedKP = {};
    categoryStrings.forEach((cat) => {
      if (keyPlayers[cat]) {
        updatedKP[cat] = keyPlayers[cat].map((player) => ({
          ...player,
          value: player.name,
          name: player.name,
        }));
      }
    });
    setUpdatedKeyPlayers(updatedKP);

    const updatedSelected = {};
    categoryStrings.forEach((cat) => {
      const isUserTriggered = userRefetchCatsRef.current.has(cat);
      // True only when keyPlayers[cat] is a new array that arrived AFTER the trigger
      const hasFreshData = isUserTriggered &&
        keyPlayers[cat] !== preRefetchSnapshotRef.current[cat];

      if (auditTransactionId && selectedBrandsProducts[cat]?.length > 0) {
        updatedSelected[cat] = selectedBrandsProducts[cat];
        userRefetchCatsRef.current.delete(cat);
      } else if (hasFreshData) {
        const prevCount = selectedBrandsProducts[cat]?.length || DEFAULT_PRESELECT;
        updatedSelected[cat] = keyPlayers[cat].slice(0, Math.min(prevCount, MAX_COMPETITORS));
        userRefetchCatsRef.current.delete(cat);
        delete preRefetchSnapshotRef.current[cat];
      } else if (selectedBrandsProducts[cat]?.length > 0) {
        updatedSelected[cat] = selectedBrandsProducts[cat];
      } else if (keyPlayers[cat]?.length > 0) {
        // No prior selection (first-ever fetch for this category) — pre-select top N.
        updatedSelected[cat] = keyPlayers[cat].slice(0, DEFAULT_PRESELECT);
      }
    });

    setSelectedBrandProducts(updatedSelected);
    setCompetitorsData(updatedSelected);
  }, [keyPlayers, category]);

  function getTemplateType(settingId) {
    if (settingId == null || settingId === "") return "";
    if (!Array.isArray(userMetricSetting?.data) || userMetricSetting.data.length === 0) return "";

    const template = settingId === "default"
      ? userMetricSetting.data.find((t) => t.isDefault)
      : userMetricSetting.data.find((t) => t.id != null && String(t.id) === String(settingId));

    if (!template) return "";

    if (!Array.isArray(template.visibility) || template.visibility.length === 0) return "companies";

    const typePattern = /\{\{[\s-]*type[\s-]*\}\}/;
    const hasTypeVar = template.visibility.some((v) => typePattern.test(v.attribute ?? ""));
    return hasTypeVar ? "companies" : "";
  }

  // Handle category change - fetch competitors when new category is added
  const prevCategoryRef = useRef([]);
  const prevPrimaryMarketRef = useRef(market[0]);
  useEffect(() => {
    const prevCats = prevCategoryRef.current;
    const newCats = categoryStrings.filter((c) => !prevCats.includes(c));

    newCats.forEach((cat) => {
      if (!keyPlayers[cat]) {
        const settingId = categoryWiseMetric[cat] ?? metricSettingId ?? DefaultSet;
        const type = getTemplateType(settingId);
        getCompetitorsForCategory(cat, market[0], type);
      }
    });

    // Clean up removed categories
    const removedCats = prevCats.filter((c) => !categoryStrings.includes(c));
    if (removedCats.length > 0) {
      setCompetitorsData((prev) => {
        const updated = { ...prev };
        removedCats.forEach((c) => delete updated[c]);
        return updated;
      });
      setSelectedBrandProducts((prev) => {
        const updated = { ...prev };
        removedCats.forEach((c) => delete updated[c]);
        return updated;
      });
    }

    prevCategoryRef.current = categoryStrings;
  }, [category]);

  // Re-fetch competitors when the primary market (market[0]) changes
  useEffect(() => {
    const primaryMarket = market[0] || "General Market";
    if (primaryMarket === prevPrimaryMarketRef.current) return;
    prevPrimaryMarketRef.current = primaryMarket;
    if (!categoryStrings.length) return;

    categoryStrings.forEach((cat) => {
      userRefetchCatsRef.current.add(cat);
      preRefetchSnapshotRef.current[cat] = keyPlayers[cat];
      const settingId = categoryWiseMetric[cat] ?? metricSettingId ?? DefaultSet;
      const type = getTemplateType(settingId);
      getCompetitorsForCategory(cat, primaryMarket, type);
    });
  }, [market]);

  const prevCategoryWiseMetricRef = useRef({});
  useEffect(() => {
    if (!categoryStrings.length) {
      prevCategoryWiseMetricRef.current = {};
      return;
    }

    const changedCats = categoryStrings.filter((cat) => {
      const newId = categoryWiseMetric[cat];
      const oldId = prevCategoryWiseMetricRef.current[cat];
      return newId != null && oldId != null && String(newId) !== String(oldId);
    });

    changedCats.forEach((cat) => {
      const settingId = categoryWiseMetric[cat] ?? metricSettingId ?? DefaultSet;
      const type = getTemplateType(settingId);
      userRefetchCatsRef.current.add(cat);
      preRefetchSnapshotRef.current[cat] = keyPlayers[cat];
      getCompetitorsForCategory(cat, market[0], type);
    });

    prevCategoryWiseMetricRef.current = { ...categoryWiseMetric };
  }, [categoryWiseMetric]);

  // Keep LLM selection pinned to the baseline nano model when in baseline mode
  useEffect(() => {
    if (caMode !== "baseline" || auditTransactionId) return;
    const allowed = baselineLlmOptions.map((opt) => opt.value);
    if (!llm.every((v) => allowed.includes(v))) {
      const defaultBaselineLLM = getDefaultLLM("baseline");
      setLLM([defaultBaselineLLM]);
      setPrimaryLLM(defaultBaselineLLM);
    }
  }, [caMode, rawLlmOptions, auditTransactionId]);

  // Category-wise metric tracking
  useEffect(() => {
    if (caCtx?.state?.transactionId) return;
    if (!categoryStrings.length) {
      setCategoryWiseMetricChange([]);
      return setCategoryWiseMetric({});
    }
    setCategoryWiseMetricChange((arr) => arr.filter((cat) => categoryStrings.includes(cat)));
    setCategoryWiseMetric((prev) =>
      categoryStrings.reduce((acc, cat) => {
        acc[cat] = prev?.[cat] ?? metricSettingId ?? DefaultSet;
        return acc;
      }, {})
    );
  }, [category]);

  useEffect(() => {
    if (!llm.includes(primaryLLM)) {
      const next = llm[0] || "";
      setPrimaryLLM(next);
    }
  }, [llm]);

  // Debounce: pre-fetch competitors as user types a category name
  useEffect(() => {
    const trimmed = categoryInputText.trim();
    if (!trimmed || caCtx?.loading || isFinalTransactionCompleted) return;

    if (categoryInputDebounceRef.current) clearTimeout(categoryInputDebounceRef.current);

    categoryInputDebounceRef.current = setTimeout(() => {
      if (!keyPlayers[trimmed]) {
        const settingId = metricSettingId ?? DefaultSet;
        const type = getTemplateType(settingId);
        getCompetitorsForCategory(trimmed, market[0], type);
      }
    }, 1500);

    return () => {
      if (categoryInputDebounceRef.current) clearTimeout(categoryInputDebounceRef.current);
    };
  }, [categoryInputText]);

  // Auto-preselect competitors for the currently typed category (pre-commit state)
  useEffect(() => {
    const inputCat = categoryInputText.trim();
    if (!inputCat || categoryStrings.includes(inputCat)) return;
    if (!keyPlayers[inputCat]?.length) return;
    if (selectedBrandsProducts[inputCat]?.length > 0) return;

    const autoSelected = keyPlayers[inputCat].slice(0, DEFAULT_PRESELECT).map((p) => ({
      ...p,
      value: p.name,
      name: p.name,
    }));
    setSelectedBrandProducts((prev) => ({ ...prev, [inputCat]: autoSelected }));
    setCompetitorsData((prev) => ({ ...prev, [inputCat]: autoSelected }));
  }, [keyPlayers, categoryInputText]);

  // Reload form when history record is loaded
  useEffect(() => {
    if (auditTransactionId && caCtx.formData) {
      const normalizedLLM =
        Array.isArray(caCtx.llm) && caCtx.llm.length === 1 && caCtx.llm[0] === testLLMModel
          ? ["gpt-5.6"]
          : caCtx.llm;
      const loadedPersonas =
        caCtx?.persona?.length > 0
          ? caCtx.persona
          : [{ title: "General Persona", description: "General Persona" }];

      setUpdatedKeyPlayers({});
      setCustomCompetitors({});
      setKeyPlayers({});
      prevCategoryRef.current = [];

      setCategory(caCtx.category.map((c) => ({ category: c })));
      setCategoryInputText(caCtx.category[0] || "");
      setCategoryWiseMetric(caCtx.state?.metricSettingObject?.categoryWiseMetric ?? {});
      setPersonaSettings(caCtx.state.personaSettings);
      setMarket(caCtx.markets);
      setPersona(loadedPersonas);
      setPersonaMap((prev) => {
        const merged = { ...prev };
        loadedPersonas.forEach(({ title, description }) => {
          if (title && !merged[title]) merged[title] = description || title;
        });
        return merged;
      });
      setLLM(normalizedLLM);
      setPrimaryLLM(caCtx.primary_llm === testLLMModel ? "gpt-5.6" : caCtx.primary_llm);
      setMetricSettingId(caCtx.customTemplateID ?? "default");
      setRbiType(caCtx.state.rbiType || "propensityToRecommend");
      setKeywords(caCtx.state?.keywords?.length > 0 ? caCtx.state.keywords : resolveDefaultKeywordOptions(customerUiInfo));
      setSelectedMetrics(caCtx.state?.selectedMetrics?.length > 0 ? caCtx.state.selectedMetrics : ALL_METRIC_VALUES);
      setSelectedBrandProducts(caCtx.competitors);
      setCompetitorsData(caCtx.competitors);
      setIsBatchMode(!!caCtx.state?.isBatchMode);
      setRankCompetitiveSet(caCtx.state?.rankCompetitiveSet ?? []);
      setRankMethodology(caCtx.state?.rankMethodology ?? "by_category");
      caCtx.updateFormData(false);
    }
  }, [caCtx.formData, caCtx.state?.isBatchMode, caCtx.state?.rankCompetitiveSet, caCtx.state?.rankMethodology]);

  // Reset form on cancel
  useEffect(() => {
    if ((cancelTransaction && !caCtx.abort_launch) || caCtx.is_resetFormState) {
      const normalizedLLM =
        Array.isArray(caCtx.llm) && caCtx.llm.length === 1 && caCtx.llm[0] === testLLMModel
          ? ["gpt-5.6"]
          : caCtx.llm;
      setCategory(caCtx.category.map((c) => ({ category: c })));
      setCategoryInputText(caCtx.category[0] || "");
      setMarket(["General Market"]);
      setPersonaSettings({ applyPersonaToAllCategory: false, applyPersonaToAllMarkets: false });
      setPersona([{ title: "General Persona", description: "General Persona" }]);
      setLLM(normalizedLLM);
      setPrimaryLLM(getDefaultLLM());
      setMetricSettingId(defaultUseMetricSettingID);
      setCancelTransaction(false);
      setIsAuditRunning(false);
      caCtx.resetFormState(false);
      setRbiType(caCtx.state.rbiType || "propensityToRecommend");
      setKeywords(caCtx.state?.keywords?.length > 0 ? caCtx.state.keywords : resolveDefaultKeywordOptions(customerUiInfo));
      setSelectedMetrics(caCtx.state?.selectedMetrics?.length > 0 ? caCtx.state.selectedMetrics : ALL_METRIC_VALUES);
      setIsBatchMode(false);
      setRankCompetitiveSet([]);
      setRankMethodology("by_category");
    }
  }, [cancelTransaction, caCtx.abort_launch, caCtx.is_resetFormState]);

  // Compare form to detect changes from loaded history record
  const normalizeCompetitors = (data) => {
    const result = {};
    Object.keys(data || {})
      .filter((key) => key !== "default")
      .sort()
      .forEach((key) => {
        result[key] = (data[key] || [])
          .map((comp) => comp?.name?.toLowerCase().replace(/\s+/g, "") || "")
          .sort();
      });
    return result;
  };

  const compareFormData = () => {
    if (!caCtx?.state?.transactionId) return false;
    const original = {
      market: caCtx.markets || [],
      category: caCtx.category || [],
      competitor: normalizeCompetitors(caCtx.competitors || {}),
      customTemplateID: Number(caCtx.customTemplateID),
      persona: caCtx.persona || [],
    };
    const current = {
      market,
      category: categoryStrings,
      competitorsData: normalizeCompetitors(competitorsData || {}),
      customTemplateID: Number(metricSettingId),
      persona,
    };
    const isMarketSame =
      JSON.stringify([...original.market].sort()) === JSON.stringify([...current.market].sort());
    const isCategorySame =
      JSON.stringify([...original.category].sort()) === JSON.stringify([...current.category].sort());
    const isPersonaSame =
      JSON.stringify([...original.persona].sort()) === JSON.stringify([...current.persona].sort());
    const isCompetitorSame =
      JSON.stringify(original.competitor) === JSON.stringify(current.competitorsData);
    const isCustomTemplateIDSame = String(original.customTemplateID) === String(current.customTemplateID);
    return !(isMarketSame && isCategorySame && isCompetitorSame && isPersonaSame && isCustomTemplateIDSame);
  };

  useEffect(() => {
    setIsChanged(compareFormData());
  }, [category, market, persona, competitorsData, metricSettingId, caCtx.category]);

  // --- Persona handlers ---
  async function handleAddPersona(formData) {
    const title = formData?.title?.trim();
    const description = formData?.description?.trim() || title;
    if (!title) return;
    if (Object.keys(personaMap).some((key) => key.toLowerCase() === title.toLowerCase())) {
      toast.warning("This persona already exists");
      return;
    }
    const customerId = user?.customer_id;
    if (customerId) {
      await dispatch(createPersona(customerId, { persona_name: title, persona_description: description })).then(() =>
        dispatch(fetchPersonas(customerId))
      );
    }
    setPersonaMap((prev) => ({ ...prev, [title]: description }));
    if (persona.length >= maxPersonas) { toast.warn(`Only ${maxPersonas} Persona selections are allowed`); return; }
    setPersona((prev) => [...prev, { title, description }]);
  }

  function handleChipClickPersona(title) {
    const matchedRecord = customerPersonasData?.find((item) => item?.persona?.name === title);
    setEditingPersonaTitle(title);
    setEditingCustomerPersonaId(matchedRecord?.id ?? null);
    setPersonaEditModalOpen(true);
  }

  function handleUpdatePersona(formData) {
    const newTitle = formData?.title?.trim() || editingPersonaTitle;
    const description = formData?.description?.trim() || editingPersonaTitle;
    if (
      newTitle.toLowerCase() !== editingPersonaTitle.toLowerCase() &&
      Object.keys(personaMap).some((key) => key.toLowerCase() === newTitle.toLowerCase())
    ) {
      toast.warning("This persona already exists");
      return;
    }
    if (editingCustomerPersonaId) {
      const matchedRecord = customerPersonasData?.find((item) => item?.id === editingCustomerPersonaId);
      const customerId = matchedRecord?.customer_id;
      if (customerId) {
        dispatch(updatePersona(customerId, editingCustomerPersonaId, { persona_name: newTitle, persona_description: description })).then(() =>
          dispatch(fetchPersonas(customerId))
        );
      }
    }
    setPersonaMap((prev) => {
      const updated = { ...prev };
      if (newTitle !== editingPersonaTitle) delete updated[editingPersonaTitle];
      updated[newTitle] = description;
      return updated;
    });
    setPersona((prev) =>
      prev.map((p) => {
        const t = typeof p === "string" ? p : p?.title;
        return t === editingPersonaTitle ? { title: newTitle, description } : p;
      })
    );
    setPersonaEditModalOpen(false);
    setEditingPersonaTitle(null);
    setEditingCustomerPersonaId(null);
  }

  function handlePersonaSelectionChange(selectedTitles) {
    if (selectedTitles.length > maxPersonas) {
      toast.warn(`Only ${maxPersonas} Persona selections are allowed`);
      const capped = selectedTitles.slice(0, maxPersonas);
      setPersona(capped.map((title) => ({ title, description: personaMap[title] || title })));
      return;
    }
    setPersona(selectedTitles.map((title) => ({ title, description: personaMap[title] || title })));
  }

  function handleCategoryInputAdd() {
    const trimmed = categoryInputText.trim();
    if (!trimmed) return;
    setCategory([{ category: trimmed }]);
  }

  const debouncedCompetitorValidation = useRef(
    lodash.debounce(async (inputValue, categoryName) => {
      if (!inputValue?.trim()) return;
      if (competitorValidationAbortRef.current) competitorValidationAbortRef.current.abort();
      const controller = new AbortController();
      competitorValidationAbortRef.current = controller;
      setModalLoading(true);
      setCompetitorWarning("");
      try {
        const response = await validateBrandInputValue(inputValue, controller.signal, categoryName);
        if (controller.signal.aborted) return;
        const data = response?.data?.data;
        if (data?.ans === "Yes") {
          setCustomCompetitorValues({ domain: data.domain || "example.com", variants: [] });
        } else {
          setCompetitorWarning(`Entered competitor doesn't belong to the "${categoryName}" category.`);
        }
      } catch (e) {
        if (e.name !== "AbortError") console.error("Competitor validation error:", e);
      } finally {
        if (!competitorValidationAbortRef.current?.signal?.aborted) setModalLoading(false);
      }
    }, 1000)
  ).current;

  // --- Competitor handlers ---
  function handleCompetitorSelection(players, categoryName, options) {
    let mergedOptions = options;
    if (customCompetitorValues?.domain) {
      const newComp = {
        value: players[players.length - 1],
        name: players[players.length - 1],
        domain: customCompetitorValues?.domain || "example.com",
        variants: customCompetitorValues?.variants || [],
      };
      const alreadyExists = options.some((opt) => opt.value === newComp.value);
      if (alreadyExists) {
        setCustomCompetitorValues({});
        toast.warning("This competitor is already added for this category.");
        return;
      }
      mergedOptions = [...options, newComp];
      setCustomCompetitors((prev) => {
        const updated = { ...prev };
        const list = updated[categoryName] || [];
        if (!list.some((item) => item.value === newComp.value)) {
          updated[categoryName] = [...list, newComp];
        }
        return updated;
      });
      setCustomCompetitorValues({});
    }

    let updatedPlayers = players.map((player) => {
      const matched = mergedOptions.find((item) => item.name === player);
      return { value: player, name: player, domain: matched?.domain || "example.com", variants: matched?.variants ?? [] };
    });

    if (updatedPlayers.length > MAX_COMPETITORS) {
      const baseMsg = `Only ${MAX_COMPETITORS} competitors are allowed`;
      const showVisibilityHint = !isUncappedCompetitorAccount && !isVisibilityOnlyRun;
      toast.warn(showVisibilityHint ? `${baseMsg}. Select Visibility only to run up to 10.` : baseMsg);
      updatedPlayers = updatedPlayers.slice(0, MAX_COMPETITORS);
    }

    setSelectedBrandProducts((prev) => ({ ...prev, [categoryName]: updatedPlayers }));
    setCompetitorsData((prev) => ({ ...prev, [categoryName]: updatedPlayers }));
  }

  function handleDeleteCompetitor(itemName, categoryName) {
    setSelectedBrandProducts((prev) => ({
      ...prev,
      [categoryName]: (prev[categoryName] || []).filter((item) => item.name !== itemName),
    }));
    setCompetitorsData((prev) => ({
      ...prev,
      [categoryName]: (prev[categoryName] || []).filter((c) => c.name.toLowerCase() !== itemName.toLowerCase()),
    }));
  }

  // --- Template handlers ---
  function categoryWiseTemplateHandle(cat) {
    setCategoryWiseMetricChange((list) => Array.from(new Set([...list, cat])));
  }

  function CategoryWiseTemplateChangeHandle({ category: cat, value }) {
    if (isTransactionPending) return;
    setCategoryWiseMetric((prev) => ({ ...prev, [cat]: value }));
    categoryWiseTemplateHandle(cat);
  }

  function MetricSettingChangeHandle(id) {
    if (isTransactionPending) return;
    setMetricSettingId(id);
    setCategoryWiseMetricChange([]);
    setCategoryWiseMetric(
      categoryStrings.reduce((acc, cat) => { acc[cat] = id ?? DefaultSet; return acc; }, {})
    );
  }

  // --- Cancel / delete handlers ---
  const cancelTransactionRecord = () => {
    stopPolling();
    setCancelTransaction(true);
    caCtx.updateAbortLaunch(true);
  };

  // const handleStartNew = () => {
  //   stopPolling();
  //   caCtx.resetState();
  //   setCategory([]);
  //   setLLM(["gpt-5.6"]);
  //   setPrimaryLLM("gpt-5.6");
  //   setMarket(["General Market"]);
  //   setPersona([{ title: "General Persona", description: "General Persona" }]);
  //   setPersonaSettings({ applyPersonaToAllCategory: false, applyPersonaToAllMarkets: false });
  //   setMetricSettingId(defaultUseMetricSettingID);
  //   setKeyPlayers({});
  //   setUpdatedKeyPlayers({});
  //   setSelectedBrandProducts({});
  //   setCompetitorsData({});
  //   setCustomCompetitors({});
  //   setIsChanged(false);
  //   setIsAuditRunning(false);
  //   setCancelTransaction(false);
  //   onCAModeChange(isBaselineCategoryAnalysisEnabled ? "baseline" : "full");
  //   onReset();
  // };

  const isUrlFromDifferentUser = (() => {
    const _uid = new URLSearchParams(window.location.search).get("user_id");
    return !!_uid && String(user?.user_role?.user_id) !== String(_uid);
  })();

  const handleStartNew = () => {
    stopPolling();
    setIsAuditRunning(false);
    if (isFinalTransactionCompleted) {
      // Analysis completed — keep selected values, refresh competitor suggestions per category
      caCtx.resetForRelaunch();
      if (llmOptions?.length && llm.some((v) => !llmOptions.some((opt) => opt.value === v))) {
        setLLM([getDefaultLLM()]);
        setPrimaryLLM(getDefaultLLM());
      }
      if (categoryStrings.length > 0) {
        setKeyPlayers({});
        categoryStrings.forEach((cat) => {
          const settingId = categoryWiseMetric[cat] ?? metricSettingId ?? DefaultSet;
          const type = getTemplateType(settingId);
          getCompetitorsForCategory(cat, market[0], type);
        });
      }
      const templateBelongsToUser =
        metricSettingId === "default" ||
        (userMetricSetting?.data ?? []).some((t) => String(t.id) === String(metricSettingId));
      if (isUrlFromDifferentUser || !templateBelongsToUser) {
        caCtx.updateCustomTemplateID(null);
        setMetricSettingId(defaultUseMetricSettingID);
        setCategoryWiseMetric({});
        setCategoryWiseMetricChange([]);
      }
    } else {
      // Not completed — clear the form entirely
      caCtx.resetForRelaunch();
      setCategory([]);
      setCategoryInputText("");
      setLLM([getDefaultLLM()]);
      setPrimaryLLM(getDefaultLLM());
      setMarket(["General Market"]);
      setPersona([{ title: "General Persona", description: "General Persona" }]);
      setKeyPlayers({});
      setSelectedBrandProducts({});
      setCompetitorsData({});
      setCustomCompetitors({});
      setCustomCompetitorValues({});
      caCtx.updateCustomTemplateID(null);
      setMetricSettingId(defaultUseMetricSettingID);
    }
    onStartNew();
  };

  async function deleteTransaction() {
    setShowDeleteDialog(false);
    await deleteHistory(auditTransactionId);
    setIsAuditRunning(false);
    stopPolling();
    if (shouldResetOnDelete) {
      caCtx.resetState();
      setCategory([]);
      setLLM([getDefaultLLM()]);
      setPrimaryLLM(getDefaultLLM());
      setMarket(["General Market"]);
      setPersona([{ title: "General Persona", description: "General Persona" }]);
      setMetricSettingId(defaultUseMetricSettingID);
      setKeyPlayers({});
      setSelectedBrandProducts({});
      setCompetitorsData({});
      onReset();
    } else {
      caCtx.resetState();
      onReset();
    }
    getHistory(page, 5);
  }

  // --- Launch ---
  const debounceLaunch = lodash.debounce(async function () {
    if (!effectiveCategoryStrings.length || !llm) {
      toast.warn("Please fill all required fields");
      return;
    }
    // Auto-commit typed category if user hasn't pressed Enter
    if (categoryStrings.length === 0 && categoryInputText.trim()) {
      setCategory([{ category: categoryInputText.trim() }]);
    }
    if (selectedMetrics.includes("rank") && (!keywords || keywords.length === 0)) {
      toast.warn("Please select at least one rank attribute");
      return;
    }
    setLaunchMessage(false);
    setIsChanged(false);

    const allCategoryCompetitorNames = Object.values(competitorsData || {})
      .flat()
      .map((c) => c?.name)
      .filter(Boolean);

    const submitValues = {
      category: effectiveCategoryStrings,
      market,
      persona,
      llm: launchType === "test" ? [testLLMModel] : llm,
      primaryLLM: launchType === "test" ? testLLMModel : primaryLLM,
      competitorsData,
      metricSettingId,
      categoryWiseMetric,
      rbiType,
      personaSettings,
      keywords,
      selectedMetrics,
      personaMap,
      caMode,
      isBatchMode: launchType === "test" ? false : isBatchMode,
      rankMethodology,
      rankCompetitiveSet: user?.is_custom_rank
        ? Array.from(new Set([...allCategoryCompetitorNames, ...rankCompetitiveSet]))
        : [],
    };

    onSubmit(submitValues);
  }, 1000);

  const hasRequiredCompetitors =
    effectiveCategoryStrings.length > 0 &&
    effectiveCategoryStrings.every((cat) => {
      const comps = competitorsData[cat] || [];
      return comps.length >= 1;
    });
  const hasRequiredLLMs = llm && llm.length >= 1;
  const hasFormValues =
    auditTransactionId ||
    effectiveCategoryStrings.length > 0 ||
    llm.length > 1 || llm[0] !== getDefaultLLM() ||
    market.length > 1 || market[0] !== "General Market";

  // Render competitor multiselect for a given category
  const renderCompetitors = (categoryName) => {
    const normalize = (str = "") => str.toLowerCase().replace(/\s+/g, "");
    const competitorOptions = updatedKeyPlayers[categoryName] ?? [];
    const contextData =
      competitorsData[categoryName]?.map((player) => ({ ...player, value: player.name, name: player.name })) ?? [];
    const contextNames = new Set(contextData.map((c) => normalize(c.name)));
    const merged = [
      ...contextData,
      ...competitorOptions.filter((c) => !contextNames.has(normalize(c.name))),
    ];
    const customList = Array.isArray(customCompetitors?.[categoryName]) ? customCompetitors[categoryName] : [];
    let options = merged.slice(0, MAX_COMPETITORS_OPTIONS);
    const existingNames = new Set(options.map((opt) => normalize(opt.name)));
    const uniqueCustomList = customList.filter((c) => !existingNames.has(normalize(c.name)));
    options = [...options, ...uniqueCustomList];

    return (
      <div style={{ flex: 1 }} key={categoryName}>
        <MUIMultiSelect
          loading={keyPlayersLoading}
          disabled={caCtx?.loading || isFinalTransactionCompleted}
          fullWidth={true}
          customWidth={classes.fullWidth}
          allowCustomOption={true}
          optionValue={""}
          label={`${categoryName} ${ENTITY_LABELS.competitor}s`}
          value={selectedBrandsProducts[categoryName]?.map((obj) => obj.name) || []}
          options={caCtx?.loading || isFinalTransactionCompleted ? [] : options}
          allowSettingPrimary={false}
          onChange={(e) => handleCompetitorSelection(e, categoryName, options)}
          onCustomConfirm={(finalValue) => {
            if (!finalValue) setCustomCompetitorValues({});
          }}
          modalValidation={{
            modalLoading,
            errorMessage: competitorWarning,
            errorTitle: "Mismatched brand or category",
            setErrorMessage: setCompetitorWarning,
            onCustomInputChange: (inputValue) => debouncedCompetitorValidation(inputValue, categoryName),
          }}
        />
        {competitorsData[categoryName]?.length < 1 && (
          <Typography color="textDisabled" variant="caption" sx={{ fontSize: "8px" }}>
            Select competitors{" "}
            <Typography color="error" variant="caption" sx={{ fontSize: "8px" }}>
              (minimum 1, up to {MAX_COMPETITORS})
            </Typography>
          </Typography>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={classes.card}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.radio}>
            {isAssessmentUiOn(user) && isFlagOn(user?.is_brandaudit_enabled) ? (
              <RadioGroup
                disabled={caCtx.loading}
                options={AUDIT_TYPE_OPTIONS}
                selectedvalue="category"
                onChange={handleAuditTypeChange}
              />
            ) : (
              <Typography variant="subtitle1" fontWeight={600}>Category Analysis</Typography>
            )}
            {isBaselineCategoryAnalysisEnabled && (
              <MUISelect
                disabled={caCtx.loading}
                options={CA_MODE_OPTIONS}
                optionValue={caMode}
                onChange={(e) => {
                  const newMode = e.target.value;
                  onCAModeChange(newMode);
                  if (newMode === "baseline") {
                    const baselineMaxCompetitors = 3;
                    const defaultBaselineLLM = getDefaultLLM("baseline");
                    setLLM([defaultBaselineLLM]);
                    setPrimaryLLM(defaultBaselineLLM);
                    // Trim competitor selections to baseline limit per category
                    setSelectedBrandProducts((prev) => {
                      const updated = {};
                      Object.keys(prev).forEach((cat) => {
                        updated[cat] = (prev[cat] || []).slice(0, baselineMaxCompetitors);
                      });
                      return updated;
                    });
                    setCompetitorsData((prev) => {
                      const updated = {};
                      Object.keys(prev).forEach((cat) => {
                        updated[cat] = (prev[cat] || []).slice(0, baselineMaxCompetitors);
                      });
                      return updated;
                    });
                  } else {
                    const defaultFullLLM = getDefaultLLM("full");
                    setLLM([defaultFullLLM]);
                    setPrimaryLLM(defaultFullLLM);
                  }
                }}
                size="small"
                style={{ minWidth: 160, marginLeft: 16 }}
              />
            )}
          </div>
          <div className={classes.header_actions}>
            {warningText && <Warning text={warningText} onClose={() => setWarningText("")} />}
            <MUIButton
              variant="outlined"
              size="small"
              onClick={() => setShowHistoryModal(true)}
            >
              <span style={{ marginRight: "6px", fontSize: "14px" }}>🕐</span>
              View History
            </MUIButton>
            <MUIIconButton
              sx={{ color: "#000", marginTop: "-10px !important" }}
              onClick={() => setShowConfigModal(true)}
            >
              <SettingsOutlined />
            </MUIIconButton>
          </div>
        </div>

        <div className={classes.input_group}>
          {/* Row 1: Category + LLM + Market + Persona */}
          <div className={classes.input_section}>
            {/* Category Input */}
            <div style={{ flex: 1 }}>
              {isMultiCategoryEnabled ? (
                <MUIMultiItemBox
                  label={ENTITY_LABELS.category}
                  value={category}
                  onChange={(newVal) => {
                    setCategory(newVal);
                  }}
                  max={MAX_CATEGORIES}
                  labelKey="category"
                  modalTitle={`Add ${ENTITY_LABELS.category}`}
                  modalFields={[{ name: "category", label: `${ENTITY_LABELS.category} Name`, required: true }]}
                  isDisabled={caCtx?.loading || isFinalTransactionCompleted}
                />
              ) : (
                <TextInput
                  fullWidth
                  label={ENTITY_LABELS.category}
                  placeholder="Athletic Apparel"
                  value={categoryInputText}
                  disabled={caCtx?.loading || isFinalTransactionCompleted}
                  onChange={(e) => setCategoryInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCategoryInputAdd();
                    }
                  }}
                />
              )}
            </div>

            <div style={{ flex: 1 }}>
              <MUIMultiSelect
                disabled={caCtx?.loading || isFinalTransactionCompleted || (!isNewUser && !isFinalLaunchEnabled)}
                label={"LLM"}
                allowSettingPrimary={true}
                options={caCtx?.loading || isFinalTransactionCompleted ? [llm] : displayLlmOptions}
                loading={!caCtx?.loading && !isFinalTransactionCompleted && llmOptionsLoading}
                value={shouldLockToTestLLM ? [testLLMModel] : (llm || ["gpt-5.6"])}
                customWidth={classes.fullWidth}
                renderSelectedValues={(selected) =>
                  selected.map((val) => OVERVIEW_LLM_LABELS[val] || val || "gpt-5.6").join(", ")
                }
                onRadioChange={(val) => setPrimaryLLM(val)}
                selectedPrimary={shouldLockToTestLLM ? testLLMModel : (primaryLLM || "gpt-5.6")}
                onChange={(selected, primary) => {
                  if (selected && selected.length > MAX_LLMS) {
                    toast.warn(`Only ${MAX_LLMS} LLM selections are allowed in ${caMode === "baseline" ? "Test Analysis" : "Full Analysis"}`);
                    setLLM([...llm]);
                    return;
                  }
                  setLLM(selected);
                  setPrimaryLLM(primary);
                }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <MUIMultiItemBox
                label={ENTITY_LABELS.market}
                value={market}
                onChange={setMarket}
                max={3}
                modalTitle={`Add ${ENTITY_LABELS.market}`}
                modalFields={[{ name: "market", label: ENTITY_LABELS.market, required: true }]}
                isDisabled={caCtx?.loading || isFinalTransactionCompleted}
              />
            </div>

            <div style={{ flex: 1 }}>
              <MUIMultiSelect
                disabled={caCtx?.loading || isFinalTransactionCompleted}
                label={`${ENTITY_LABELS.persona}s`}
                allowSettingPrimary={false}
                allowCustomOption={true}
                customWidth={classes.fullWidth}
                options={caCtx?.loading || isFinalTransactionCompleted ? [] : personaOptions}
                value={selectedPersonaTitles}
                modalFields={[
                  { name: "title", label: `${ENTITY_LABELS.persona} Name`, required: true },
                  { name: "description", label: `Details for ${ENTITY_LABELS.persona}`, required: true, multiline: true, rows: 3 },
                ]}
                onModalSave={handleAddPersona}
                onChipClick={caCtx?.loading || isFinalTransactionCompleted ? null : handleChipClickPersona}
                onChange={(selectedTitles) => handlePersonaSelectionChange(selectedTitles)}
              />
            </div>
          </div>

          {/* Row 2: Competitors + Template */}
          <div className={classes.input_section_with_template}>
            {categoryStrings.length > 0
              ? categoryStrings.map((cat) => renderCompetitors(cat))
              : (() => {
                const inputCat = categoryInputText.trim();
                const normalize = (str = "") => str.toLowerCase().replace(/\s+/g, "");
                const rawOptions = (keyPlayers[inputCat] ?? []).map((p) => ({ ...p, value: p.name, name: p.name }));
                const contextData = (competitorsData[inputCat] ?? []).map((p) => ({ ...p, value: p.name, name: p.name }));
                const contextNames = new Set(contextData.map((c) => normalize(c.name)));
                const merged = [...contextData, ...rawOptions.filter((c) => !contextNames.has(normalize(c.name)))];
                const customList = Array.isArray(customCompetitors?.[inputCat]) ? customCompetitors[inputCat] : [];
                let preOptions = merged.slice(0, MAX_COMPETITORS_OPTIONS);
                const existingNames = new Set(preOptions.map((o) => normalize(o.name)));
                preOptions = [...preOptions, ...customList.filter((c) => !existingNames.has(normalize(c.name)))];

                return (
                  <div style={{ flex: 1 }}>
                    <MUIMultiSelect
                      loading={keyPlayersLoading}
                      disabled={caCtx?.loading || isFinalTransactionCompleted}
                      fullWidth={true}
                      customWidth={classes.fullWidth}
                      allowCustomOption={true}
                      optionValue={""}
                      label="Competitors"
                      value={selectedBrandsProducts[inputCat]?.map((o) => o.name) || []}
                      options={caCtx?.loading || isFinalTransactionCompleted ? [] : preOptions}
                      allowSettingPrimary={false}
                      onChange={(e) => handleCompetitorSelection(e, inputCat, preOptions)}
                      onCustomConfirm={(finalValue) => {
                        if (!finalValue) setCustomCompetitorValues({});
                      }}
                      modalValidation={{
                        modalLoading,
                        errorMessage: competitorWarning,
                        errorTitle: "Mismatched brand or category",
                        setErrorMessage: setCompetitorWarning,
                        onCustomInputChange: (inputValue) => debouncedCompetitorValidation(inputValue, inputCat),
                      }}
                    />
                    {competitorsData[inputCat]?.length < 1 && (
                      <Typography color="textDisabled" variant="caption" sx={{ fontSize: "8px" }}>
                        Select competitors{" "}
                        <Typography color="error" variant="caption" sx={{ fontSize: "8px" }}>
                          (minimum 1, up to {MAX_COMPETITORS})
                        </Typography>
                      </Typography>
                    )}
                  </div>
                );
              })()
            }
            <div style={{ flex: 1 }}>
              <MUISelect
                loading={userMetricSetting.loading}
                disabled={caCtx?.loading || isFinalTransactionCompleted || isTransactionPending}
                optionValue={metricSettingId === "default" ? "default" : Number(metricSettingId)}
                label="Template"
                options={caTemplateOptions}
                CustomOption={(opt) => (
                  <span style={{ fontWeight: opt.isDefault ? 700 : 400 }}>{opt.name}{opt.isDefault ? " ★" : ""}</span>
                )}
                onChange={(e) => MetricSettingChangeHandle(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={classes.action_buttons}>
          {!isFinalTransactionCompleted && (
            <>
              <MUIButton
                variant="contained"
                color={"primary"}
                disabled={
                  !effectiveCategoryStrings.length ||
                  !hasRequiredCompetitors ||
                  !hasRequiredLLMs ||
                  caCtx.loading ||
                  isAuditRunning ||
                  isTransactionPending
                }
                onClick={() => {
                  setLaunchType("test");
                  setLaunchMessage(true);
                }}
              >
                Run Test Analysis
              </MUIButton>

              {caCtx?.state?.launchRecord?.length > 0 && (
                <MUIButton
                  variant="contained"
                  color="primary"
                  disabled={
                    !categoryStrings.length ||
                    !hasRequiredCompetitors ||
                    !hasRequiredLLMs ||
                    caCtx.loading ||
                    (isChanged && !caCtx.loading) ||
                    isAuditRunning ||
                    isTransactionPending ||
                    !isFinalLaunchEnabled
                  }
                  onClick={() => {
                    setLaunchType("final");
                    setLaunchMessage(true);
                  }}
                >
                  Run Final Analysis
                </MUIButton>
              )}
            </>
          )}

          {caCtx?.state?.finalTransactionStatus !== "COMPLETED" && auditTransactionId && (
            <MUIButton
              color="error"
              onClick={() => setShowDeleteDialog(true)}
            >
              Cancel
            </MUIButton>
          )}

          <MUIButton
            variant="outlined"
            color="primary"
            onClick={handleStartNew}
            disabled={!hasFormValues}
          >
            Start New
          </MUIButton>
        </div>
      </div>

      {/* Launch confirmation modal */}
      {launchMessage && (
        <Modal
          open={launchMessage}
          handleClose={() => setLaunchMessage(false)}
          handleConfirm={() => {
            debounceLaunch();
            setLaunchMessage(false);
          }}
          showActions
          title={launchType === "test" ? "Run Test Analysis" : "Run Final Analysis"}
          confirmBtnText="Launch"
        >
          <div style={{ padding: "1rem" }}>
            {launchType === "test"
              ? "This will run a test category analysis using a low-cost model. Results will be available shortly."
              : "This will run a full category analysis using your selected LLMs. Processing may take some time to complete."}
          </div>
        </Modal>
      )}

      {auditTransactionId && (
        <Modal
          handleClose={() => setShowDeleteDialog(false)}
          handleConfirm={deleteTransaction}
          open={showDeleteDialog}
          title="Cancel Analysis"
          showActions
          confirmBtnText="Delete"
          confirmBtnColor="error"
        >
          <div className={classes.contentText}>
            {shouldResetOnDelete
              ? `Do you want to delete this analysis - ${categoryStrings[0] || ""}?`
              : `Do you want to delete the current test for ${categoryStrings[0] || ""}?`}
          </div>
        </Modal>
      )}

      {showHistoryModal && (
        <CAHistory
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          setActiveTab={setActiveTab}
          setIsAuditRunning={setIsAuditRunning}
          startPolling={startPolling}
          stopPolling={stopPolling}
          setCancelTransaction={setCancelTransaction}
          loadCategoryAnalysis={loadCategoryAnalysis}
          onCAModeChange={onCAModeChange}
          isBaselineCategoryAnalysisEnabled={isBaselineCategoryAnalysisEnabled}
          onBaselineCAHtmlReportChange={onBaselineCAHtmlReportChange}
        />
      )}

      {/* Settings Modal */}
      <CASettingsModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        category={categoryStrings}
        categoryWiseMetric={categoryWiseMetric}
        metricSettingId={metricSettingId}
        onCategoryTemplateChange={CategoryWiseTemplateChangeHandle}
        MetricSettingChangeHandle={MetricSettingChangeHandle}
        personaSettings={personaSettings}
        setPersonaSettings={setPersonaSettings}
        rbiType={rbiType}
        setRbiType={setRbiType}
        keywords={keywords}
        setKeywords={setKeywords}
        selectedMetrics={selectedMetrics}
        setSelectedMetrics={setSelectedMetrics}
        isBatchMode={isBatchMode}
        onBatchModeChange={setIsBatchMode}
        llm={shouldLockToTestLLM ? [testLLMModel] : llm}
        rankCompetitiveSet={rankCompetitiveSet}
        onRankCompetitiveSetChange={setRankCompetitiveSet}
        rankMethodology={rankMethodology}
        onRankMethodologyChange={setRankMethodology}
        competitorsData={competitorsData}
        keyPlayers={updatedKeyPlayers}
        keyPlayersLoading={keyPlayersLoading}
      />

      {/* Persona edit modal */}
      {personaEditModalOpen && (
        <Modal
          open={personaEditModalOpen}
          handleClose={() => { setPersonaEditModalOpen(false); setEditingPersonaTitle(null); setEditingCustomerPersonaId(null); }}
          handleConfirm={handleUpdatePersona}
          title={`Edit ${ENTITY_LABELS.persona}`}
          showActions
          confirmBtnText="Save"
          fields={[
            { name: "title", label: `${ENTITY_LABELS.persona} Name`, required: true, defaultValue: editingPersonaTitle },
            { name: "description", label: `${ENTITY_LABELS.persona} Description`, required: true, multiline: true, rows: 3, defaultValue: personaMap[editingPersonaTitle] || "" },
          ]}
        />
      )}
      <ToastContainer />
    </>
  );
}
